// src/components/pages/Cuestionarios.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCourseWithQuizzes,
  getFlattenedQuizzesByCourse,
} from "../Stores/quizzesStore";

const Cuestionarios = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const course = getCourseWithQuizzes(courseId || "");
  const allQuizzes = useMemo(
    () => (course ? getFlattenedQuizzesByCourse(courseId) : []),
    [course, courseId]
  );

  const [search, setSearch] = useState("");
  const [versionFilter, setVersionFilter] = useState("todas");
  const [pageSize, setPageSize] = useState(10);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Cada vez que cambian filtros, regresamos a la página 1
  useEffect(() => {
    setPage(1);
  }, [search, versionFilter, pageSize, moduleFilter, courseId]);

  const filtrados = useMemo(() => {
    let data = allQuizzes;

    if (moduleFilter !== "all") {
      data = data.filter((q) => q.moduloId === moduleFilter);
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      data = data.filter((q) => q.nombre.toLowerCase().includes(term));
    }

    if (versionFilter !== "todas") {
      data = data.filter((q) => String(q.version) === versionFilter);
    }

    return data;
  }, [allQuizzes, moduleFilter, search, versionFilter]);

  const total = filtrados.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageItems = filtrados.slice(start, end);

  const handleGoPage = (n) => {
    if (n < 1 || n > totalPages) return;
    setPage(n);
  };

  const handleEdit = (quizId) => {
    navigate(`/menu/lms/cuestionarios/${courseId}/${quizId}`);
  };

  if (!course) {
    return (
      <div className="q-page">
        <div className="profile-header-title">
          <h1>Cuestionarios</h1>
        </div>
        <p>No se encontró el curso seleccionado.</p>
      </div>
    );
  }

  return (
    <div className="q-page">
      <div className="profile-header-title">
        <h1>Cuestionarios</h1>
        <p className="q-subtitle">
          Curso: <strong>{course.titulo}</strong>
        </p>
      </div>

      {/* barra de acciones */}
      <div className="q-header-line">
        <div className="q-actions-left">
          <button className="q-btn q-btn-primary">
            Crear nuevo cuestionario
          </button>
          <button className="q-btn q-btn-secondary">
            Asignar cuestionario a un área
          </button>
        </div>

        <button className="q-btn q-btn-success">Exportar a Excel</button>
      </div>

      {/* Filtro por módulos */}
      <div className="q-modules-row">
        <button
          type="button"
          className={
            "q-module-pill" + (moduleFilter === "all" ? " active" : "")
          }
          onClick={() => setModuleFilter("all")}
        >
          Todos los módulos
        </button>
        {course.modulos.map((m) => (
          <button
            key={m.id}
            type="button"
            className={
              "q-module-pill" + (moduleFilter === m.id ? " active" : "")
            }
            onClick={() => setModuleFilter(m.id)}
          >
            {m.nombre}
          </button>
        ))}
      </div>

      {/* filtros */}
      <div className="q-filters-row">
        <div className="q-search">
          <input
            type="text"
            placeholder="Buscar cuestionario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="q-select"
          value={versionFilter}
          onChange={(e) => setVersionFilter(e.target.value)}
        >
          <option value="todas">Todas las versiones</option>
          <option value="2025">Versión 2025</option>
        </select>

        <div className="q-page-size">
          <span className="q-label-inline">Mostrar:</span>
          <select
            className="q-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
        </div>
      </div>

      {/* tabla */}
      <div className="q-table-wrapper">
        <table className="q-table">
          <thead>
            <tr>
              <th style={{ width: "13%" }}>Tipo de evaluación</th>
              <th>Nombre de la evaluación</th>
              <th style={{ width: "20%" }}>Módulo</th>
              <th style={{ width: "8%" }}>Versión</th>
              <th style={{ width: "20%" }}>Fecha de creación</th>
              <th style={{ width: "17%" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((q) => (
              <tr key={q.id}>
                <td>
                  <span className="q-type-pill">{q.tipo}</span>
                </td>
                <td>{q.nombre}</td>
                <td className="q-module-cell">{q.moduloNombre}</td>
                <td>{q.version}</td>
                <td>{q.fechaCreacion}</td>
                <td>
                  <div className="q-actions-cell">
                    <button
                      className="q-action-btn q-action-edit"
                      onClick={() => handleEdit(q.id)}
                    >
                      Editar
                    </button>
                    <button
                      className="q-action-btn q-action-questions"
                      onClick={() => handleEdit(q.id)}
                    >
                      Agregar preguntas
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {total === 0 && (
              <tr>
                <td colSpan={6} className="q-empty">
                  No se encontraron cuestionarios con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* pie tabla + paginación */}
      <div className="q-footer-row">
        <span className="q-footer-info">
          Mostrando {total === 0 ? 0 : start + 1}–{Math.min(end, total)} de{" "}
          {total} cuestionarios
        </span>

        <div className="q-pagination">
          <button
            className="q-page-btn"
            disabled={safePage === 1}
            onClick={() => handleGoPage(safePage - 1)}
          >
            Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              className={
                "q-page-btn" + (n === safePage ? " q-page-btn-active" : "")
              }
              onClick={() => handleGoPage(n)}
            >
              {n}
            </button>
          ))}

          <button
            className="q-page-btn"
            disabled={safePage === totalPages}
            onClick={() => handleGoPage(safePage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cuestionarios;
